import { IStoreState, ThunkDispatch } from '../../modules';
import React, { Component, Fragment } from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import { deleteUser, login, loginFailure, logout, newUser, unlockUser } from '../../modules/auth';

import Button from '@material-ui/core/Button';
import { ChallengeResult } from '../../modules/challenge';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { Tooltip } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { proxyBase } from '../../modules/config';
import { push } from 'connected-react-router';
import { reset as resetPayees } from '../../modules/payees';

//const backgroundUrl = "https://source.unsplash.com/featured/daily"
const backgroundUrl = "/background-money-compressed.jpg"

const styles = (theme: Theme) => createStyles({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  loginGrid: {
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  loginPanel: {
    position: "relative",
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backgroundImage: `url("${backgroundUrl}")`,
    backgroundPosition: 'bottom center',
    backgroundSize: 'cover',
    backgroundAttachment: 'fixed',
    padding: `${theme.spacing(4)}px ${theme.spacing(3)}px ${theme.spacing(5)}px`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    zIndex: 1,
    "& > *": {
      zIndex: 1
    },
    "&::before": {
      content: "' '",
      background: "inherit",
      position: "absolute",
      left: "-25px",
      right: "-25px",
      top: "-25px",
      bottom: "-25px",
      boxShadow: "inset 0 0 0 3000px rgba(255,255,255,0.5)",
      filter: "blur(10px)",
      zIndex: 0,
    },
  },
  form: {
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      maxWidth: '300px',
    }
  },
  formProgress: {
    opacity: 0.5,
  },
  progressModal: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  copyright: {
    textAlign: 'center',
    color: '#eee',
    textShadow: '0px 1px 3px #666',
    marginBottom: theme.spacing(2)
  },
  passwordTooltip: {
    fontSize: '0.8rem',
    lineHeight: '1rem',
  }
});

type State = {
  redirectToReferrer: boolean
  username: string
  password: string
  progress: boolean
  devicePrint: string
  error: string,
};

class Login extends Component<Props, State> {
  state = {
    redirectToReferrer: false,
    username: "",
    password: "",
    progress: false,
    devicePrint: "",
    error: "",
  }

  componentDidMount = () => {
    const { auth, logout } = this.props

    document.title = "Login"

    if (auth.authenticated)
      logout()

  }

  onUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      username: e.target.value
    })
  }
  onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      password: e.target.value
    })
  }

  async formSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const { username, password } = this.state;
    const { login, loginFailure, newUser, unlockUser, deleteUser, proxyBase, config } = this.props;

    this.setState({ progress: true, error: "" })


    if (["proxy", "direct"].includes(config.aa.mode || "")) {
      if (password.toLowerCase().startsWith('reset')) {
        await deleteUser(username)
        await newUser(username)
      }
      else if (password.toLowerCase().startsWith('unlock')) {
        await unlockUser(username)
      }
      else if (password.toLowerCase().startsWith('delete')) {
        await deleteUser(username)
        this.setState({ error: "User deleted", progress: false })
        return
      }
    }

    if (password.toLowerCase().startsWith('error')) {
      loginFailure(username).then(() => {
        this.setState({ progress: false, error: 'Incorrect username/password' })
      })
    }
    else {
      let { analyzePromise, challengePromise } = login(username)

      analyzePromise.then(data => {
        this.setState({ progress: false })
        return data
      })
        .then(data => {
          if (data.actionCode === "DENY")
            throw new Error("Login denied")
          return data
        })
        .then(data => {
          return challengePromise.then(result => {
            if (result === ChallengeResult.Failure)
              throw new Error("Challenge failed")
            if (result === ChallengeResult.Error)
              throw new Error("Challenge error")
            return data
          })
        })
        .then(data => {
          this.props.resetPayees()

          let userStatus = _.get(data.soapResponse, 'identificationData.userStatus', null)
          let bindingType = _.get(data.soapResponse, 'deviceResult.deviceData.bindingType', null)

          if (["NOTENROLLED", "DELETE"].includes(userStatus)) {
            // user is now in UNVERIFIED state
            return newUser(username).then((data: any) => {
              this.props.push(proxyBase('/enroll'))
              return data
            })
          }

          this.setState({ progress: false })

          if (userStatus === "UNVERIFIED") {
            this.props.push(proxyBase('/enroll'))
          }
          else if (bindingType === "NONE")
            this.props.push(proxyBase('/bind'))
          else
            this.props.push(proxyBase('/pib/overview'))
          return data
        })
        .catch(error => {
          console.error(error)
          this.setState({ error: error.message, progress: false })
        })
    }
  }

  render() {
    const { classes, config } = this.props;
    const { redirectToReferrer, progress, error } = this.state;

    return (
      <Fragment>
        {redirectToReferrer &&
          <Redirect to="/pib" />
        }
        <div className={classes.root}>
          <Grid container direction="row" alignItems="flex-start" spacing={0} className={classNames(classes.loginGrid, 'maxWidth')}>
            <div style={{ width: '100%', height: '1px', position: 'absolute', top: '40px', left: '0', 'zIndex': 1000 }}>test</div>
            <Grid item xs={12} sm={8} md={5} className={classes.loginPanel}>

              <Typography variant="h5">Sign In</Typography>
              {error.length > 0 &&
                <Typography className='error'>{error}</Typography>
              }
              <form className={classNames(classes.form, (progress ? classes.formProgress : null))} onSubmit={this.formSubmit.bind(this)}>
                <FormControl margin="normal" required fullWidth>
                  <InputLabel htmlFor="username">Username</InputLabel>
                  <Input id="username" name="username" autoComplete="username" onChange={this.onUsernameChange.bind(this)} autoFocus />
                </FormControl>
                <Tooltip placement="left" disableFocusListener={true} title={
                  <div className={classes.passwordTooltip}>All passwords will result in a successful login, except passwords starting with:
                    <ul>
                      <li><strong>error</strong> - Generates an authentication error (wrong password)</li>
                      {["direct", "proxy"].includes(config.aa.mode || "") &&
                        <Fragment>
                          <li><strong>unlock</strong> - Unlocks the user</li>
                          <li><strong>reset</strong> - Deletes and recreates the user</li>
                          <li><strong>delete</strong> - Deletes the user</li>
                        </Fragment>
                      }
                    </ul>
                  </div>} enterDelay={500}>
                  <FormControl margin="normal" required fullWidth>
                    <InputLabel htmlFor="password">Password</InputLabel>
                    <Input name="password" type="password" id="password" onChange={this.onPasswordChange.bind(this)} autoComplete="current-password" />
                  </FormControl>
                </Tooltip>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  style={{ marginTop: '2rem' }}
                >
                  Sign in
                </Button>
                <Button
                  fullWidth
                  color="primary"
                  style={{ marginTop: '1rem' }}
                >
                  Forgot Password
                </Button>
                <div className={classes.progressModal} style={{ display: (progress ? 'flex' : 'none') }}>
                  <CircularProgress />
                </div>
              </form>
            </Grid>
          </Grid>
        </div>
        <style>
          {`
          body {
            background-image: url("${backgroundUrl}");
          }
          `}
        </style>
      </Fragment>
    )
  }
}


const mapStateToProps = ({ auth, config }: IStoreState) => ({
  auth,
  config
})

const mapDispatchToProps = (dispatch: ThunkDispatch) =>
  bindActionCreators(
    {
      login,
      logout,
      loginFailure,
      push,
      newUser,
      unlockUser,
      deleteUser,
      proxyBase,
      resetPayees,
    }, dispatch
  )

interface Props extends WithStyles<typeof styles>, ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps>, RouteComponentProps<{}> {
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Login));